import type { TFunction } from '~/core/i18n'

import { leftpad } from '~/shared/helpers'
import { getTimerTranslations } from './translations'

/**
 * Encode token into Base64 string
 *
 *   | id = ed7464cc-991a-4b85-afad-76cbc4ae422c
 *   | time = 15
 *   | unit: MINUTE
 *   ↓
 *   ed7464cc-991a-4b85-afad-76cbc4ae422c|1661401003|1661401903
 *   ↓
 *   ZWQ3NDY0Y2MtOTkxYS00Yjg1LWFmYWQtNzZjYmM0YWU0MjJjfDE2NjE0MDEwMDN8MTY2MTQwMTkwMw==
 *
 * Could throw an exception, should be wrapped in try/catch
 * Should add more obfuscation?
 */
export function token(id: string, time: number, unit: string) {
  const from = Math.floor(Date.now() / 1000) // current time in seconds
  const to = from + time * ({ MINUTE: 60, HOUR: 3600 }[unit] ?? 1) // `id`s end of lifetime
  return btoa(id + '|' + from + '|' + to)
}

/**
 * Encode new token instead old one
 *
 * Could throw an exception, should be wrapped in try/catch
 * Should add more obfuscation?
 */
export function retoken(id: string, to: number) {
  const from = Math.floor(Date.now() / 1000) // current time in seconds
  return btoa(id + '|' + from + '|' + to)
}

/**
 * Get id, time, unit and timestamp from token
 *
 *   ZWQ3NDY0Y2MtOTkxYS00Yjg1LWFmYWQtNzZjYmM0YWU0MjJjfDE2NjE0MDEwMDN8MTY2MTQwMTkwMw==
 *   ↓
 *   ed7464cc-991a-4b85-afad-76cbc4ae422c|1661401003|1661401903
 *   ↓
 *   | id = ed7464cc-991a-4b85-afad-76cbc4ae422c
 *   | from = 1661401003
 *   | to = 1661401903
 *
 * Could throw an exception, should be wrapped in try/catch
 * Should add more obfuscation?
 */
export function untoken(token: string) {
  const [id, sfrom, sto] = atob(token).split('|')
  return { id, from: Number(sfrom), to: Number(sto) }
}

/**
 * Humanize token allowed resend request time, depending on token request time
 * TODO: i18n?
 */
export function humanizeFrom(from: number | null, limit = 60) {
  if (from === null || isNaN(from)) return null // meaning allow to request code again

  // `from` in seconds, so multiply by 1000, and then get seconds by dividing by 1000
  let diff = Math.floor((Date.now() - from * 1000) / 1000)
  if (diff >= limit) return null // meaning allow to request code again
  diff = limit - diff // invert for coundown

  const hours = Math.floor(diff / 3600) // get hours
  diff %= 3600

  const minutes = Math.floor(diff / 60) // get minutes
  diff %= 60

  const seconds = diff // get seconds

  return (
    (hours > 0 ? leftpad(hours, 2, '0') + ':' : '') +
    leftpad(minutes, 2, '0') +
    ':' +
    leftpad(seconds, 2, '0')
  )
}

/**
 * Humanize token `id`'s end of life to remaining time
 * TODO: i18n?
 */
export function humanizeTo(to: number | null, t?: TFunction) {
  if (to === null || isNaN(to)) return undefined // meaning `to` is unknown

  // `to` in seconds, so multiply by 1000, and then get seconds by dividing by 1000
  let diff = Math.floor((to * 1000 - Date.now()) / 1000)
  if (diff <= 0) return null // meaning uuid has been expired
  if (!t) return ''
  const { hoursKey, hourKey, minutesKey, minuteKey, secondsKey, secondKey } =
    getTimerTranslations(t)

  let h = ''
  const hours = Math.floor(diff / 3600) // get diff in hours
  if (hours > 1) h = hours + ` ${hoursKey}`
  if (hours === 1) h = `1 ${hourKey}`
  diff -= hours * 3600

  const minutes = Math.ceil(diff / 60) // get diff in minutes
  if (minutes === 60)
    return hours > 0 ? hours + 1 + ` ${hoursKey}` : `1 ${hourKey}`
  if (hours > 0 && minutes > 1) return h + ' ' + minutes + ` ${minutesKey}`
  if (hours > 0 && minutes === 1) return h + ' ' + minutes + ` ${minuteKey}`
  if (hours > 0) return h
  if (minutes > 1) return minutes + ` ${minutesKey}`

  if (diff > 1) return diff + ` ${secondsKey}`
  return `1 ${secondKey}`
}
